import {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { MiraCustomerData } from '../../../../../shared/types/customer';
import { getMiraCustomersWithDiscounts } from '../../../services/customers';
import {
  deleteTransportCustomersBatch,
  upsertTransportCustomersBatch,
} from '../../../services/transportCustomers';
import CustomerImportActions from './CustomerImportActions';
import CustomerImportConfirmChangesDialog from './CustomerImportConfirmChangesDialog';
import CustomerImportSummary from './CustomerImportSummary';
import CustomerImportTable from './CustomerImportTable';
import {
  buildComparisonRows,
  buildInitialSelection,
  buildSyncPayloads,
} from './customerImportUtils';
import {
  ComparisonRow,
  Customer,
  TransportCustomerPayload,
} from './customerImportTypes';
import NotificationProvider from '../../../providers/Notification/NotificationProvider';

type CustomerImportDialogProps = {
  open: boolean;
  customers: Customer[];
  onClose: () => void;
};

function CustomerImportDialog({
  open,
  customers,
  onClose,
}: CustomerImportDialogProps) {
  const queryClient = useQueryClient();
  const hasInitializedSelection = useRef(false);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [selectionByKey, setSelectionByKey] = useState<
    Record<string, boolean>
  >({});
  const [confirmChangesOpen, setConfirmChangesOpen] = useState(false);
  const [pendingSyncPayload, setPendingSyncPayload] = useState<{
    upserts: TransportCustomerPayload[];
    deleteIds: number[];
  } | null>(null);

  const { setNotification } = useContext(NotificationProvider);

  const {
    data: miraCustomersWithDiscounts = [],
    isLoading: isMiraLoading,
    isError: isMiraError,
  } = useQuery<MiraCustomerData[]>({
    queryKey: ['miraCustomersWithDiscounts'],
    queryFn: getMiraCustomersWithDiscounts,
    enabled: open,
  });

  const syncCustomersMutation = useMutation({
    mutationFn: async ({
      upserts,
      deleteIds,
    }: {
      upserts: TransportCustomerPayload[];
      deleteIds: number[];
    }) => {
      if (upserts.length > 0) {
        await upsertTransportCustomersBatch(upserts);
      }
      if (deleteIds.length > 0) {
        await deleteTransportCustomersBatch(deleteIds);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['transportCustomers'],
      });
      onClose();
      setSelectionByKey({});
      setNotification({
        type: 'SNACKBAR',
        duration: 2000,
        severity: 'success',
        message: 'Asiakastiedot päivitetty onnistuneesti',
      });
    },
    onError: (error) => {
      setNotification({
        severity: 'error',
        message: 'Asiakastietojen päivitys epäonnistui',
      });
      console.error('Failed to sync customers:', error);
    },
  });

  const comparisonRows = useMemo(
    () => buildComparisonRows(customers, miraCustomersWithDiscounts),
    [customers, miraCustomersWithDiscounts],
  );

  const effectiveRows = useMemo(() => {
    return comparisonRows.filter((row) =>
      showUnchanged ? true : row.status !== 'unchanged',
    );
  }, [comparisonRows, showUnchanged]);

  const counts = useMemo(() => {
    return comparisonRows.reduce(
      (acc, row) => {
        acc[row.status] += 1;
        return acc;
      },
      { add: 0, modify: 0, remove: 0, unchanged: 0 },
    );
  }, [comparisonRows]);

  const selectedCounts = useMemo(() => {
    return comparisonRows.reduce(
      (acc, row) => {
        if (!selectionByKey[row.key]) {
          return acc;
        }
        acc[row.status] += 1;
        return acc;
      },
      { add: 0, modify: 0, remove: 0, unchanged: 0 },
    );
  }, [comparisonRows, selectionByKey]);

  const errorCount = useMemo(() => {
    return comparisonRows.filter((row) => row.hasMissingData).length;
  }, [comparisonRows]);

  useEffect(() => {
    if (!open) {
      hasInitializedSelection.current = false;
      return;
    }

    if (isMiraLoading || hasInitializedSelection.current) {
      return;
    }

    const initial = buildInitialSelection(comparisonRows);

    setSelectionByKey(initial);
    setShowUnchanged(false);
    hasInitializedSelection.current = true;
  }, [comparisonRows, isMiraLoading, open]);

  function toggleAll(checked: boolean) {
    const next: Record<string, boolean> = {};
    effectiveRows.forEach((row) => {
      next[row.key] =
        row.status !== 'unchanged' &&
        !row.hasMissingData &&
        checked &&
        !row.invalidReason;
    });
    setSelectionByKey((prev) => ({
      ...prev,
      ...next,
    }));
  }

  function toggleRow(row: ComparisonRow) {
    if (row.status === 'unchanged') {
      return;
    }
    if (row.hasMissingData) {
      return;
    }
    setSelectionByKey((prev) => ({
      ...prev,
      [row.key]: !prev[row.key],
    }));
  }

  const hasSelection = effectiveRows.some(
    (row) => selectionByKey[row.key],
  );

  function handleConfirmSync() {
    if (!pendingSyncPayload) {
      return;
    }

    syncCustomersMutation.mutate(pendingSyncPayload);
    setConfirmChangesOpen(false);
    setPendingSyncPayload(null);
  }

  function handleCloseDialog() {
    onClose();
  }

  function handleToggleShowUnchanged() {
    setShowUnchanged(function toggleShowUnchanged(prev) {
      return !prev;
    });
  }

  function handleSelectAll() {
    toggleAll(true);
  }

  function handleDeselectAll() {
    toggleAll(false);
  }

  function handleSyncClick() {
    const { upserts, deleteIds } = buildSyncPayloads(
      comparisonRows,
      selectionByKey,
    );
    if (upserts.length === 0 && deleteIds.length === 0) {
      return;
    }
    setPendingSyncPayload({ upserts, deleteIds });
    setConfirmChangesOpen(true);
  }

  function handleCancelSyncConfirm() {
    setConfirmChangesOpen(false);
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Tuo asiakastiedot Mirasta</DialogTitle>
        <DialogContent>
          <CustomerImportSummary
            counts={counts}
            errorCount={errorCount}
            showUnchanged={showUnchanged}
            onToggleShowUnchanged={handleToggleShowUnchanged}
            isMiraLoading={isMiraLoading}
            isMiraError={isMiraError}
          />
          <CustomerImportActions
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
          />
          <CustomerImportTable
            rows={effectiveRows}
            isLoading={isMiraLoading}
            selectionMap={selectionByKey}
            onToggleRow={toggleRow}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="outlined"
          >
            Sulje
          </Button>
          <Button
            onClick={handleSyncClick}
            color="primary"
            variant="contained"
            disabled={
              syncCustomersMutation.isPending || !hasSelection
            }
          >
            {syncCustomersMutation.isPending
              ? 'Päivitetään...'
              : 'Vahvista muutokset'}
          </Button>
        </DialogActions>
      </Dialog>
      <CustomerImportConfirmChangesDialog
        open={confirmChangesOpen}
        onCancel={handleCancelSyncConfirm}
        onConfirm={handleConfirmSync}
        isPending={syncCustomersMutation.isPending}
        counts={selectedCounts}
      />
    </>
  );
}

export default CustomerImportDialog;
